
import { MetaInfo } from 'vue-meta'
import { Component, Prop, Vue } from 'vue-property-decorator'
import PuCampsitePriceCard from '~/components/campsite/campsite-card/PuCampsitePriceCard.vue'
import { PaginationData } from '~/utility/pagination'
import { defaultPageSize } from '../defaults'
import type { CampsiteResult, SearchFilters } from '../schemas'

@Component({
  components: {
    PuCampsitePriceCard,
  },
  head: function () {
    return this.head()
  },
})
export default class SearchResultsList extends Vue {
  @Prop()
    paginationData?: PaginationData

  @Prop()
    campsites: CampsiteResult[]

  @Prop()
    searchFilters: SearchFilters

  @Prop()
    isPointSearch: boolean

  visibleCampsites: Set<string> = new Set()

  get forceQueryParams() {
    return ['lat', 'lng', 'placename', 'within']
  }

  get nonBookableCampsitesCount() {
    return this.campsites.filter(
      (cs) => cs.status === 'free' && !cs.availableToSearch,
    ).length
  }

  get zeroAvailabilityCampsitesCount() {
    return this.campsites.filter((cs) => cs.bookable && !cs.availableToSearch)
      .length
  }

  isCurrentCampsiteFirstNonBookableOnTheList(index: number) {
    return (
      this.nonBookableCampsitesCount &&
      this.campsites[index - 1] &&
      this.campsites[index - 1].bookable &&
      !this.campsites[index].bookable
    )
  }

  isCurrentCampsiteFirstZeroAvailabilityOnTheList(index: number) {
    return (
      this.zeroAvailabilityCampsitesCount &&
      this.campsites[index - 1] &&
      this.campsites[index - 1].availableToSearch &&
      !this.campsites[index].availableToSearch
    )
  }

  get query() {
    const removeParams = ['page', 'point', 'h', 'hierarchy']
    const remove = removeParams.reduce((a, b) => (a[b] = undefined) || a, {})
    return Object.assign({}, this.$route.query, remove)
  }

  get showPaginationControls() {
    return (
      this.paginationData &&
      this.paginationData.totalCount >
        (this.searchFilters.limit || defaultPageSize)
    )
  }

  get currentPage() {
    return this.paginationData && this.paginationData.currentPage
  }

  get totalPages() {
    return this.paginationData && this.paginationData.totalPages
  }

  get showPaginationSummary() {
    return this.paginationData && this.paginationData.currentPage > 1
  }

  campsiteToCampsitePriceCardProps(campsite: CampsiteResult) {
    return {
      ...campsite,
      hierarchyStringShort: campsite.hierarchyStringShortCountry,
      primaryPhoto: campsite.primaryPhoto,
      isPointSearch: this.isPointSearch,
      searchFilters: this.searchFilters,
    }
  }

  previousY: number = 0
  previousRatio: number = 0
  visibilityChanged(isVisible, entry) {
    if (this.$isDesktop) return

    const currentY = entry.boundingClientRect.y
    const currentRatio = entry.intersectionRatio
    const isIntersecting = entry.isIntersecting

    if (isVisible) {
      this.$emit('visibleCampsiteChange', {
        slug: entry.target.dataset.slug,
        point: entry.target.dataset.point,
      })
    }

    if (currentY < this.previousY) {
      if (currentRatio > this.previousRatio && isIntersecting) {
        this.$emit('scrolledResultsDown', false)
      }
    } else if (
      currentY > this.previousY &&
      isIntersecting &&
      currentRatio > this.previousRatio
    ) {
      this.$emit('scrolledResultsDown', true)
    }
    this.previousY = currentY
    this.previousRatio = currentRatio
  }

  mounted() {
    if (this.$isDesktop) {
      this.setUpResultCardListeners()
    }
  }

  setUpResultCardListeners() {
    document.querySelectorAll('li.search-card').forEach((li) => {
      li.addEventListener(
        'mouseenter',
        () => {
          document
            .querySelectorAll('.search-card.map-marker-opened')
            .forEach((el) => {
              el.classList.remove('map-marker-opened')
            })
          const dataSlug = li.getAttribute('data-slug')
          if (dataSlug) {
            this.$emit('visibleCampsiteChange', {
              slug: li.getAttribute('data-slug'),
              point: li.getAttribute('data-point'),
            })
          }
        },
        { passive: true },
      )
    })
  }

  head(): MetaInfo {
    if (!this.paginationData) return {}
    return {
      script: [
        {
          type: 'application/ld+json',
          body: true,
          json: {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            url: `https://${this.$config.public.envUrl}${this.$route.path}`,
            itemListOrder: 'http://schema.org/ItemListOrderAscending',
            numberOfItems: this.paginationData?.totalCount,
            itemListElement: this.campsites.map((campsite, index) => {
              const pageIndexBase =
                ((this.paginationData?.currentPage || 1) - 1) *
                (this.searchFilters.limit || defaultPageSize)
              const h = campsite.hierarchyPath.split('/')
              const url = this.$puLocalePath({
                name: 'campsite-detail',
                params: {
                  h1: h[0],
                  h2: h[1],
                  h3: h[2],
                  h4: h[3],
                  slug: campsite.slug,
                },
              })
              return {
                '@type': 'ListItem',
                position: index + 1 + pageIndexBase,
                url: `https://${this.$config.public.envUrl}${url}`,
              }
            }),
          },
        },
      ],
    }
  }
}
